import { InfoBubble } from '@/components/InfoBubble';
import { PhoneInput } from '@/components/PhoneInput';
import { TextInput } from '@/components/TextInput';
import { cn } from '@/utils/cn';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import { TFunction } from 'i18next';

interface FormValues {
  url: string;
}

/**
 * Renders dynamic form fields based on the icon type
 * @param iconType - The type of icon (mail, call, number, address, etc.)
 * @param t - Translation function
 * @returns JSX element with the appropriate form fields
 */
export const RenderLinkFields = ({
  icon,
  t,
  type,
}: {
  icon: string | null;
  t: TFunction;
  type: string;
}) => {
  const { errors, touched } = useFormikContext<FormValues>();
  if (!icon) return null;

  const isCustomLink = type === 'custom_link';
  const renderHelper = (icon: string) => (
    <InfoBubble
      message={t(`icon:${!isCustomLink ? icon : 'custom_link'}_helper`)}
      className="mt-1"
    />
  );

  switch (icon.toLowerCase()) {
    case 'mail':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600">
              {t('updateCard:links.email_address')}
              <span className="ml-1 text-danger">*</span>
            </label>
            <Field
              name="url"
              type="email"
              className={cn(
                `w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 shadow-sm transition-colors focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200`,
              )}
              placeholder={t(
                `icon:${!isCustomLink ? icon : 'custom_link'}_placeholder`,
              )}
            />
            <ErrorMessage
              name="url"
              component="div"
              className="mt-1 text-sm text-red-500"
            />
            {renderHelper(icon)}
          </div>
        </div>
      );

    case 'call':
    case 'message':
    case 'whatsapp':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600">
              {t('updateCard:links.phone_number')}
              <span className="ml-1 text-danger">*</span>
            </label>
            <PhoneInput
              name="url"
              placeholder="000 00 00 00"
              error={!!errors?.url && !!touched?.url}
              defaultCountry={touched?.url ? undefined : 'BE'}
            />

            {renderHelper(icon)}
          </div>
        </div>
      );

    case 'address':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600">
              {t('updateCard:links.address')}
              <span className="ml-1 text-danger">*</span>
            </label>
            <Field
              name="url"
              as="textarea"
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 shadow-sm transition-colors focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
              placeholder={t(
                `icon:${!isCustomLink ? icon : 'custom_link'}_placeholder`,
              )}
            />
            <ErrorMessage
              name="url"
              component="div"
              className="mt-1 text-sm text-red-500"
            />
            {renderHelper(icon)}
          </div>
        </div>
      );

    default:
      return (
        <div className="space-y-4">
          <div className="mb-4">
            <TextInput
              name="url"
              type="text"
              label={t('updateCard:links.url')}
              placeholder={t(
                `icon:${!isCustomLink ? icon : 'custom_link'}_placeholder`,
              )}
              isRequired
              helper={t(`icon:${!isCustomLink ? icon : 'custom_link'}_helper`)}
            />
          </div>
        </div>
      );
  }
};
